import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Typography,
  Paper,
  Checkbox,
  Box,
  Chip,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import CommonButton from "../../../Components/InputComponents/CommonButton";
import SelectInput from "../../../Components/InputComponents/SelectInput";
import {
  errorNotify,
  infoNotify,
  successNotify,
} from "../../../Components/ToastComponent/ToastNotification";
import { axiosAPI } from "../../../Components/AxiosComponents/Axios";

const AddDocumentProcedure = ({ maxwidth = "none" }) => {
  const isMobile = useMediaQuery("(max-width: 1220px)");
  const navigate = useNavigate();
  const [isDisabled, setIsDisabled] = useState(false);
  const [formData, setFormData] = useState({
    country: "",
    profession: "",
  });

  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [errors, setErrors] = useState({});

  const [countries, setCountries] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [documentStages, setDocumentStages] = useState([]);

  const [loader, setLoader] = useState({ country: true, profession: true });

  const getCountries = async () => {
    try {
      setLoader((prev) => ({ ...prev, country: true }));
      const res = await axiosAPI(`/RecruitmentCountry/list`);
      setCountries(res?.data?.data || []);
    } catch (err) {
      console.error(err);
      errorNotify("Failed to load countries.");
    } finally {
      setLoader((prev) => ({ ...prev, country: false }));
    }
  };

  const getProfessions = async () => {
    try {
      setLoader((prev) => ({ ...prev, profession: true }));
      const res = await axiosAPI(`/Profession/active/list`);
      setProfessions(res?.data?.data || []);
    } catch (err) {
      console.error(err);
      errorNotify("Failed to load professions.");
    } finally {
      setLoader((prev) => ({ ...prev, profession: false }));
    }
  };

  const getDocumentStages = async () => {
    try {
      const res = await axiosAPI.get(`/DocChecklist/active/list`);
      setDocumentStages(res?.data?.data || []);
    } catch (err) {
      console.error("Active API failed, retrying without 'active'", err);
      try {
        const resFallback = await axiosAPI.get(`/DocChecklist/list`);
        setDocumentStages(resFallback?.data?.data || []);
      } catch (fallbackErr) {
        console.error("Fallback API also failed", fallbackErr);
      }
    }
  };

  const handleChange = (name) => (event) => {
    setFormData({
      ...formData,
      [name]: event.target.value,
    });
  };

  const handleToggle = (doc) => {
    setSelectedDocuments((prev) =>
      prev.some((d) => d.DC_Id === doc.DC_Id)
        ? prev.filter((d) => d.DC_Id !== doc.DC_Id)
        : [...prev, doc]
    );
  };

  const validateForm = () => {
    const formErrors = {};

    if (!formData.country) {
      formErrors.country = "Country is required.";
    }
    if (!formData.profession) {
      formErrors.profession = "Profession is required.";
    }
    if (selectedDocuments.length === 0) {
      formErrors.selectedDocuments = "Please select at least one document.";
    }

    setErrors(formErrors);

    if (Object.keys(formErrors).length > 0) {
      Object.values(formErrors).forEach((error) => {
        errorNotify(error);
      });
      return false;
    }

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsDisabled(true); // Disable button during API call

    const payload = {
      CountryID: formData.country,
      ProfessionID: formData.profession,
      DocumentIDs: selectedDocuments.map((doc) => doc.DC_Id),
    };

    axiosAPI
      .post(`/DocChecklist/document-mapping`, payload)
      .then((res) => {
        if (res?.data?.success) {
          successNotify(res?.data?.message);
          navigate(`/DocumentProcedure`);
        } else {
          infoNotify(res?.data?.message);
        }
      })
      .catch((err) => {
        console.error("Error submitting form:", err);
        errorNotify("Something went wrong");
      })
      .finally(() => {
        setIsDisabled(false); // Always re-enable button
      });
  };

  useEffect(() => {
    getCountries();
    getProfessions();
    getDocumentStages();
  }, []);

  return (
    <Paper
      sx={{
        padding: { xs: 2, sm: 3, md: 5 },
        margin: "auto",
        position: "relative",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
        width: isMobile ? "100vw" : "calc(100vw - 280px)",
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 700, marginBottom: 3 }}>
        Add Document Checklist Mapping
      </Typography>

      <Grid
        container
        sx={{
          border: "1px solid lightgrey",
          width: "100%",
          margin: "10px auto",
          borderRadius: "10px",
        }}
        p={2}
      >
        {/* Country Dropdown */}
        <Grid item xs={12} sm={6} p={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            Country <span style={{ color: "red" }}>*</span>
          </Typography>
          <SelectInput
            placeholder="Choose Country"
            name="country"
            options={countries.map((c) => ({
              label: c?.RC_CountryName,
              value: c?.RC_Id,
            }))}
            value={formData.country}
            onChange={handleChange("country")}
            error={!!errors.country}
            loading={loader?.country}
          />
        </Grid>

        {/* Profession Dropdown */}
        <Grid item xs={12} sm={6} p={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            Profession <span style={{ color: "red" }}>*</span>
          </Typography>
          <SelectInput
            placeholder="Choose Profession"
            name="profession"
            options={professions.map((p) => ({
              label: p?.PI_Name,
              value: p?.PI_Id,
            }))}
            value={formData.profession}
            onChange={handleChange("profession")}
            error={!!errors.profession}
            loading={loader?.profession}
          />
        </Grid>

        {/* Document Checklist */}
        <Grid item xs={12} p={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            Document Checklist
          </Typography>
          <Paper
            sx={{ maxHeight: 500, overflowY: "auto", p: 4, boxShadow: "none" }}
          >
            <Grid container spacing={2}>
              {documentStages.map((doc) => {
                const isChecked = selectedDocuments.some(
                  (d) => d.DC_Id === doc.DC_Id
                );

                return (
                  <Grid item xs={12} sm={6} lg={4} key={doc.DC_Id}>
                    <Box
                      sx={{
                        border: isChecked
                          ? "2px solid #006CB8"
                          : "1px solid #ccc",
                        borderRadius: 1,
                        p: 1,
                        transition: "border-color 0.3s",
                      }}
                    >
                      <Chip
                        label={
                          <Box display="flex" alignItems="center">
                            <Checkbox
                              checked={isChecked}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggle(doc);
                              }}
                              sx={{ mr: 1 }}
                            />
                            <Typography variant="body2">
                              {doc.DC_Name}
                            </Typography>
                          </Box>
                        }
                        sx={{
                          width: "100%",
                          justifyContent: "flex-start",
                          "&:hover": {
                            backgroundColor: "#EBF2FF",
                          },
                          background: "transparent",
                        }}
                        onClick={() => handleToggle(doc)}
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Grid>

        {/* Action Buttons */}
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item>
            <CommonButton
              variant="outlined"
              customColor="#B0BEC5"
              onClick={() => navigate(`/DocumentProcedure`)}
            >
              Cancel
            </CommonButton>
          </Grid>
          <Grid item>
            <CommonButton
              onClick={handleSubmit}
              variant="contained"
              customColor="#007BFF"
              disabled={isDisabled}
            >
              {isDisabled?'Saving...':'Save'}
            </CommonButton>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AddDocumentProcedure;
